import { useReactFlow, useUpdateNodeInternals } from '@xyflow/react'
import type { UnitEntry } from 'features/assetDatabase/types/assetOverviewTypes'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoNotifications } from 'features/notifications'
import { ProcessFlowDiagramBuilderContext } from 'features/processFlowDiagrams/contexts/ProcessFlowDiagramBuilderContext'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  BaseSensorEntry,
  DropdownOption,
  PfdLabelPosition,
  PfdNode,
  PfdShapeStyle,
  RolodexEntryType,
  SensorEntryWrapper,
} from 'types'

interface PanelContextValue {
  selectedNodeId: string
  label: string
  labelPosition: PfdLabelPosition
  shapeColor: string
  textColor: string
  shapeStyle: PfdShapeStyle
  metadata: Record<string, string>
  xPosition: number
  yPosition: number
  scale: number
  rotation: number
  selectedEntryType: string
  entryID: string
  unitOptions: DropdownOption[] | undefined
  selectedTags: BaseSensorEntry[]
  setSelectedTags: React.Dispatch<React.SetStateAction<BaseSensorEntry[]>>
  displayPanel: boolean
  setDisplayPanel: (val: boolean) => void
  siteID: string
  handleLabelChange: (value: string) => void
  handleLabelPositionChange: (value: PfdLabelPosition) => void
  handleShapeColorChange: (value: string) => void
  handleTextColorChange: (value: string) => void
  handleShapeStyleChange: (value: PfdShapeStyle) => void
  handleMetadataChange: (key: string, value: string) => void
  handleMetadataRemove: (key: string) => void
  handlePositionXChange: (value: string) => void
  handlePositionYChange: (value: string) => void
  handleRotationChange: (value: string) => void
  handleSizePercentageChange: (newPercentage: number) => void
  increaseSize: () => void
  decreaseSize: () => void
  setSelectedEntryType: (value: string) => void
  handleLinkChange: (newLink: {
    entryType: string
    entryID: string
    siteID: string
  }) => void
  handleDatapointChange: (tags: BaseSensorEntry[]) => void
  datapoints: BaseSensorEntry[]
  isLoadingDatapoints: boolean
}

const NodePropertyPanelContext = createContext<PanelContextValue | undefined>(
  undefined
)

interface ProviderProps {
  selectedNode: PfdNode
  children: React.ReactNode
}

export const NodePropertyPanelProvider = ({
  selectedNode,
  children,
}: ProviderProps) => {
  const [label, setLabel] = useState(selectedNode.data.label)
  const [labelPosition, setLabelPosition] = useState<PfdLabelPosition>(
    selectedNode.data.labelPosition ?? 'bottom'
  )
  const [shapeColor, setShapeColor] = useState(
    selectedNode.data.shapeColor ?? ''
  )
  const [textColor, setTextColor] = useState(selectedNode.data.textColor ?? '')
  const [shapeStyle, setShapeStyle] = useState<PfdShapeStyle>(
    selectedNode.data.shapeStyle ?? 'outlined'
  )
  const [metadata, setMetadata] = useState<Record<string, string>>(
    selectedNode.data.metadata ?? {}
  )
  const [yPosition, setYPosition] = useState(selectedNode.position.y)
  const [xPosition, setXPosition] = useState(selectedNode.position.x)
  const [scale, setScale] = useState(100)
  const [rotation, setRotation] = useState(selectedNode.data.rotation ?? 0)
  const [selectedEntryType, setSelectedEntryType] = useState<string>('')
  const [entryID, setEntryID] = useState<string>('')

  const { displayPanel, setDisplayPanel, siteID } = useContext(
    ProcessFlowDiagramBuilderContext
  )

  const { rolodexPatch, rolodexPut } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()

  // const { data: assetLensLocations } = useGetRolodexEntriesByLoadOptions< //TODO: Uncomment this when we have a way to bind to Maximo assets
  //   {
  //     entry: BaseInvenRolodexEntryJson<RolodexEntryType.ASSET_LENS_LOCATION, GeckoAssetLensLocationJSON>
  //   }[]
  // >({
  //   type: [RolodexEntryType.ASSET_LENS_LOCATION],
  //   tags: {
  //     site: siteID,
  //   },
  //   limit: 10000,
  // })

  const {
    data: datapoints,
    isLoading: isLoadingDatapoints,
    mutate: refetchDatapoints,
  } = useGetRolodexEntriesByLoadOptions<SensorEntryWrapper[]>({
    type: [RolodexEntryType.DATA_POINTS],
    tags: {
      site: siteID,
    },
    limit: 100000,
  })

  const datapointsEntries = useMemo(
    () => datapoints?.map((datapoint) => datapoint.entry) ?? [],
    [datapoints]
  )

  const dataPointsTaggedToUnit = useMemo(
    () =>
      datapointsEntries?.filter((datapoint) => datapoint.tags.unit === entryID),
    [datapointsEntries, entryID]
  )

  const { data: unitEntries } = useGetRolodexEntriesByLoadOptions<
    { entry: UnitEntry }[]
  >({
    type: ['unit'],
    tags: { site: siteID },
    limit: 100000,
  })

  const unitOptions = useMemo(
    () =>
      unitEntries
        ?.filter((unit) => unit.entry.data)
        .map((unit) => ({
          label: unit.entry.data.name ?? unit.entry.id,
          value: unit.entry.id,
        })),
    [unitEntries]
  )

  const [selectedTags, setSelectedTags] = useState<BaseSensorEntry[]>([])

  const { setNodes } = useReactFlow<PfdNode>()
  const updateNodeInternals = useUpdateNodeInternals()

  const handleLinkChange = (newLink: {
    entryType: string
    entryID: string
    siteID: string
  }) => {
    setSelectedEntryType(newLink.entryType)
    setEntryID(newLink.entryID)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, link: newLink } }
          : node
      )
    )
  }

  const handleLabelChange = (value: string) => {
    setLabel(value)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, label: value } }
          : node
      )
    )
  }

  const handleLabelPositionChange = (value: PfdLabelPosition) => {
    setLabelPosition(value)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, labelPosition: value } }
          : node
      )
    )
  }

  const handleShapeColorChange = (value: string) => {
    setShapeColor(value)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, shapeColor: value } }
          : node
      )
    )
  }

  const handleTextColorChange = (value: string) => {
    setTextColor(value)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, textColor: value } }
          : node
      )
    )
  }

  const handleShapeStyleChange = (value: PfdShapeStyle) => {
    setShapeStyle(value)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, shapeStyle: value } }
          : node
      )
    )
  }

  const handleMetadataChange = (key: string, value: string) => {
    const newMetadata = { ...metadata, [key]: value }
    setMetadata(newMetadata)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, metadata: newMetadata } }
          : node
      )
    )
  }

  const handleMetadataRemove = (key: string) => {
    const newMetadata = { ...metadata }
    delete newMetadata[key]
    setMetadata(newMetadata)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, metadata: newMetadata } }
          : node
      )
    )
  }

  const handlePositionXChange = (value: string) => {
    const x = parseFloat(value) || 0
    setXPosition(x)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, position: { ...node.position, x } }
          : node
      )
    )
  }

  const handleRotationChange = (value: string) => {
    const newRotation = parseFloat(value) || 0
    setRotation(newRotation)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, rotation: newRotation } }
          : node
      )
    )
    updateNodeInternals(selectedNode.id)
  }

  const handlePositionYChange = (value: string) => {
    const y = parseFloat(value) || 0
    setYPosition(y)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, position: { ...node.position, y } }
          : node
      )
    )
  }

  const handleSizePercentageChange = (newPercentage: number) => {
    setScale(newPercentage)
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, scale: newPercentage / 100 } }
          : node
      )
    )
  }

  const handleDatapointChange = (tags: BaseSensorEntry[]) => {
    const datapoints = tags.map((tag) => ({
      id: tag.id,
      type: RolodexEntryType.DATA_POINTS,
      tags: {
        site: siteID,
        unit: entryID,
      },
    }))

    const datapointsToAddTag =
      datapoints.filter(
        (tag) => !dataPointsTaggedToUnit?.some((dp) => dp.id === tag.id)
      ) || []

    const datapointsToRemoveTag =
      dataPointsTaggedToUnit?.filter(
        (datapoint) => !datapoints.some((tag) => tag.id === datapoint.id)
      ) || []

    if (datapointsToAddTag.length > 0) {
      rolodexPatch(datapointsToAddTag)
        .then(() => {
          successNotification('Data points tagged successfully')
          refetchDatapoints()
        })
        .catch(() => {
          errorNotification('Failed to tag data points')
        })
    }
    if (datapointsToRemoveTag.length > 0) {
      rolodexPut(
        datapointsToRemoveTag.map((datapoint) => ({
          id: datapoint.id,
          type: RolodexEntryType.DATA_POINTS,
          data: datapoint.data,
          tags: {
            site: datapoint.tags.site,
            system: datapoint.tags.system,
            organization: datapoint.tags.organization,
          },
        }))
      )
        .then(() => {
          successNotification('Data points untagged successfully')
          refetchDatapoints()
        })
        .catch(() => {
          errorNotification('Failed to untag data points')
        })
    }
  }

  const increaseSize = () => {
    const newPercentage = Math.min(scale + 10, 1000)
    handleSizePercentageChange(newPercentage)
  }

  const decreaseSize = () => {
    const newPercentage = Math.max(scale - 10, 20)
    handleSizePercentageChange(newPercentage)
  }

  useEffect(() => {
    setSelectedTags(dataPointsTaggedToUnit)
  }, [dataPointsTaggedToUnit, entryID])

  useEffect(() => {
    setLabel(selectedNode.data.label)
    setLabelPosition(selectedNode.data.labelPosition ?? 'bottom')
    setShapeColor(selectedNode.data.shapeColor ?? '')
    setTextColor(selectedNode.data.textColor ?? '')
    setShapeStyle(selectedNode.data.shapeStyle ?? 'outlined')
    setMetadata(selectedNode.data.metadata ?? {})
    setXPosition(selectedNode.position.x)
    setYPosition(selectedNode.position.y)
    setScale(selectedNode.data.scale ? selectedNode.data.scale * 100 : 100)
    setRotation(selectedNode.data.rotation ?? 0)
    setSelectedEntryType(selectedNode.data.link?.entryType ?? '')
    setEntryID(selectedNode.data.link?.entryID ?? '')
  }, [
    selectedNode.data.label,
    selectedNode.data.labelPosition,
    selectedNode.data.shapeColor,
    selectedNode.data.textColor,
    selectedNode.data.shapeStyle,
    selectedNode.data.metadata,
    selectedNode.position.x,
    selectedNode.position.y,
    selectedNode.id,
    selectedNode.data.rotation,
    selectedNode.data.scale,
    selectedNode.data.link?.entryType,
    selectedNode.data.link?.entryID,
  ])

  const value: PanelContextValue = {
    selectedNodeId: selectedNode.id,
    label,
    labelPosition,
    shapeColor,
    textColor,
    shapeStyle,
    metadata,
    xPosition: xPosition,
    yPosition: yPosition,
    scale,
    rotation,
    selectedEntryType,
    entryID,
    unitOptions,
    selectedTags,
    setSelectedTags,
    displayPanel,
    setDisplayPanel,
    siteID,
    handleLabelChange,
    handleLabelPositionChange,
    handleShapeColorChange,
    handleTextColorChange,
    handleShapeStyleChange,
    handleMetadataChange,
    handleMetadataRemove,
    handlePositionXChange,
    handlePositionYChange,
    handleRotationChange,
    handleSizePercentageChange,
    increaseSize,
    decreaseSize,
    setSelectedEntryType,
    handleLinkChange,
    handleDatapointChange,
    isLoadingDatapoints,
    datapoints: datapointsEntries,
  }

  return (
    <NodePropertyPanelContext.Provider value={value}>
      {children}
    </NodePropertyPanelContext.Provider>
  )
}

export const useNodePropertyPanel = () => {
  const ctx = useContext(NodePropertyPanelContext)
  if (!ctx)
    throw new Error(
      'useNodePropertyPanel must be used within NodePropertyPanelProvider'
    )
  return ctx
}
